import type { ComponentType, ReactNode } from 'react';
import React from 'react';
import { flushSync } from 'react-dom';
import { createRoot, Root } from 'react-dom/client';
import { i18n as globalI18n } from '@lingui/core';

import { ToastProvider } from '@rover/kibble/official-patterns/Toast';
import BrandThemeProvider from '@rover/kibble/themes/BrandThemeProvider';
import { RSDKProviders } from '@rover/rsdk/src';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';
import ObservabilityMetadata from '@rover/rsdk/src/modules/ObservabilityMetadata';
import { RoverStatsigProvider } from '@rover/rsdk/src/modules/Statsig';
import RoverBrandProvider from '@rover/shared/js/providers/RoverBrandContextProvider';
import getRoverBrand from '@rover/utilities/getRoverBrand';
import inject from '@rover/utilities/injector';

// Django pages call this again to push new props (the phones page on every Save). A second createRoot on the
// same container makes the first root throw removeChild NotFoundError on its next update.
const rootsByElement = new WeakMap<Element, Root>();

// Callers that unmount the returned root (modals) mount into the same container again later, so an
// unmounted root must leave the cache.
function getOrCreateRoot(rootElement: Element): Root {
  const cachedRoot = rootsByElement.get(rootElement);
  if (cachedRoot) return cachedRoot;
  const root = createRoot(rootElement);
  const cacheableRoot: Root = {
    render: (children) => root.render(children),
    unmount: () => {
      rootsByElement.delete(rootElement);
      root.unmount();
    },
  };
  rootsByElement.set(rootElement, cacheableRoot);
  return cacheableRoot;
}

export default function renderRootElement<Props>(
  Component: ComponentType<Props> | ReactNode,
  elementSelector: string,
  props?: Props
): Root {
  const locale = inject('Rover.context.language', undefined);
  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
  const rootElement = document.querySelector(elementSelector);
  const brand = getRoverBrand();

  if (!rootElement) {
    throw new Error(`rootElement not found with elementSelector ${elementSelector}`);
  }

  // Get experiment user attributes from server context and convert to camelCase
  // Preserve snake_case for rover_cid and environment_id keys as they must match Statsig console configuration
  const experimentUserAttributesSnakeCase = inject(
    'Rover.context.experiment_user_attributes',
    undefined
  );
  const initialUserAttributes = experimentUserAttributesSnakeCase
    ? camelize(experimentUserAttributesSnakeCase, true, ['rover_cid', 'environment_id'])
    : undefined;

  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
  ObservabilityMetadata.set('currentView', window.location.pathname);
  ObservabilityMetadata.set('locale', locale);
  try {
    ObservabilityMetadata.set('userOPK', inject('Rover.context.person.opk', undefined));
  } catch (e) {
    // this can throw when unauthed, but we don't care
  }

  // We only want to render ReactQueryDevTools once per page, even on a Django page where renderRootElement
  // is being called multiple times for different included React components
  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
  const reactQueryDevToolsExists = document.querySelector('#ReactQueryDevtoolsPanel');

  // start the react app
  const root = getOrCreateRoot(rootElement);
  // flushSync forces sync rendering of the app https://stackoverflow.com/a/71983073
  // important in django/react apps where we may try to talk to React from other JS after calling renderRootElement
  flushSync(() => {
    root.render(
      <RSDKProviders
        shouldRenderReactQueryDevTools={!reactQueryDevToolsExists}
        locale={locale}
        // Legacy Django bundle activates this global singleton at bootstrap
        // (`app/i18n/linguiInit.ts`), one locale per page load, so resolving against it is correct here.
        i18n={globalI18n}
      >
        <RoverBrandProvider brand={brand}>
          <BrandThemeProvider brand={brand}>
            <RoverStatsigProvider initialUserAttributes={initialUserAttributes}>
              <ToastProvider>
                {/* @ts-expect-error */}
                <Component {...props} />
              </ToastProvider>
            </RoverStatsigProvider>
          </BrandThemeProvider>
        </RoverBrandProvider>
      </RSDKProviders>
    );
  });

  // We set min-height on the root container to minimize CLS. Once rendered, we can remove it
  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
  const element = document.querySelector(elementSelector);
  if (element?.getAttribute('data-should-remove-style') === 'true') {
    element?.removeAttribute('style');
  }
  return root;
}
